/**
 * Shared Epic Filters Component
 * 
 * Reusable filter controls for project and date range selection
 * used by both Epic Analysis and Epic Analytics pages.
 */

import React, { memo } from 'react'
import { ListBoxWithCheck } from '../../Elements/Controls'
import { SkeletonLoader } from '../../Elements/Indicators'
import { Card } from '../../Elements/Cards'
import YearQuarterSelector from './YearQuarterSelector'
import { XProject } from '@jirax/common'

interface DateRange {
  startDate: string
  endDate: string
}

interface SharedEpicFiltersProps {
  selectedProject: XProject | null
  dateRange: DateRange
  projects: XProject[]
  onProjectChange: (project: XProject) => void
  onDateRangeChange: (range: DateRange) => void
  loading?: boolean
  disabled?: boolean
  /** Optional custom margin class for the Card component */
  cardClassName?: string
  /** Optional custom aria-labelledby ID */
  ariaLabelledBy?: string

}

/**
 * Shared Epic Filters - Project and date range selection controls
 * Used by both Epic Analysis and Epic Analytics pages for consistency
 */
const SharedEpicFilters: React.FC<SharedEpicFiltersProps> = memo(({
  selectedProject,
  dateRange,
  projects,
  onProjectChange,
  onDateRangeChange,
  loading = false,
  disabled = false,
  cardClassName = "p-6 mb-6 overflow-visible",
  ariaLabelledBy = "epic-filters-heading"
}) => {
  // Convert projects to ListBoxItem format
  const projectOptions = projects.map(project => ({
    id: project.key,
    name: project.name,
    project // Store the full project object for easy access
  }))

  // Find selected project option
  const selectedProjectOption = selectedProject
    ? projectOptions.find(option => option.id === selectedProject.key) || projectOptions[0]
    : projectOptions[0]

  const handleProjectChange = (option: any) => {
    if (disabled) return
    if (option?.project) {
      onProjectChange(option.project)
    }
  }

  // Parse current date range to extract year and quarter
  const getYearFromDateRange = () => {
    const startDate = new Date(dateRange.startDate)
    return startDate.getFullYear()
  }

  const getQuarterFromDateRange = (): 'all' | 'q1' | 'q2' | 'q3' | 'q4' => {
    const startDate = new Date(dateRange.startDate)
    const endDate = new Date(dateRange.endDate)
    const startYear = startDate.getFullYear()
    const endYear = endDate.getFullYear()

    // If spanning multiple years, consider it "all"
    if (startYear !== endYear) return 'all'

    const startMonth = startDate.getMonth() + 1 // 1-based
    const endMonth = endDate.getMonth() + 1

    // Check if it matches a quarter exactly
    if (startMonth === 1 && endMonth === 3) return 'q1'
    if (startMonth === 4 && endMonth === 6) return 'q2'
    if (startMonth === 7 && endMonth === 9) return 'q3'
    if (startMonth === 10 && endMonth === 12) return 'q4'

    // If it's the full year
    if (startMonth === 1 && endMonth === 12) return 'all'

    // Default to all if it doesn't match a standard quarter
    return 'all'
  }

  const handleYearChange = (year: number) => {
    if (disabled) return
    const currentQuarter = getQuarterFromDateRange()
    updateDateRangeForYearQuarter(year, currentQuarter)
  }

  const handleQuarterChange = (quarter: 'all' | 'q1' | 'q2' | 'q3' | 'q4') => {
    if (disabled) return
    const currentYear = getYearFromDateRange()
    updateDateRangeForYearQuarter(currentYear, quarter)
  }

  const updateDateRangeForYearQuarter = (year: number, quarter: 'all' | 'q1' | 'q2' | 'q3' | 'q4') => {
    let startDate: string
    let endDate: string

    switch (quarter) {
      case 'q1':
        startDate = `${year}-01-01`
        endDate = `${year}-03-31`
        break
      case 'q2':
        startDate = `${year}-04-01`
        endDate = `${year}-06-30`
        break
      case 'q3':
        startDate = `${year}-07-01`
        endDate = `${year}-09-30`
        break
      case 'q4':
        startDate = `${year}-10-01`
        endDate = `${year}-12-31`
        break
      case 'all':
      default:
        startDate = `${year}-01-01`
        endDate = `${year}-12-31`
        break
    }

    onDateRangeChange({ startDate, endDate })
  }

  if (loading || !projects.length) {
    return (
      <Card className={cardClassName.replace('overflow-visible', '')}>
        <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-8">
          <div className="flex-shrink-0 lg:w-80">
            <SkeletonLoader lines={1} height="h-4" className="w-20 mb-2" />
            <SkeletonLoader lines={1} height="h-10" />
          </div>
          <div className="hidden lg:block h-10 w-px bg-gray-200 self-end mb-2"></div>
          <div className="flex-1">
            <SkeletonLoader lines={1} height="h-4" className="w-16 mb-3" />
            <SkeletonLoader lines={1} height="h-10" className="w-full" />
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card
      className={`${cardClassName} ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
      role="region"
      aria-labelledby={ariaLabelledBy}
      aria-disabled={disabled}
    >
      <h2 id={ariaLabelledBy} className="sr-only">Epic Filters</h2>
      <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-8">
        {/* Project Selection */}
        <div className="flex-shrink-0 lg:w-80">
          <ListBoxWithCheck
            label="Project"
            values={projectOptions}
            selected={selectedProjectOption}
            setSelected={handleProjectChange}
            disabled={disabled}
          />
        </div>

        {/* Separator */}
        <div className="hidden lg:block h-10 w-px bg-gray-300 self-end mb-2"></div>

        {/* Period Selection */}
        <div className="flex-1">
          <label className={`block text-sm font-medium mb-3 ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
            Period
          </label>
          <YearQuarterSelector
            selectedYear={getYearFromDateRange()}
            selectedQuarter={getQuarterFromDateRange()}
            onYearChange={handleYearChange}
            onQuarterChange={handleQuarterChange}
            disabled={disabled}
          />
        </div>
      </div>
    </Card>
  )
})

SharedEpicFilters.displayName = 'SharedEpicFilters'

export default SharedEpicFilters